/**
 * Dashboard-level user / permission management. This is the original
 * AdminPage body extracted into a panel that lives under
 * /admin/dashboard-users inside the SystemAdminLayout.
 *
 * Manages three flows:
 *  - **Invite** a brand-new account by email + role + per-permission
 *    overrides. The invite is stored locally; when that email later
 *    signs in via the upstream Support Center auth flow, the dashboard
 *    consumes the invite atomically so the user lands on their intended
 *    role from the very first session (instead of the default `viewer`).
 *  - **Edit** an existing user's dashboard role + per-user permission
 *    overrides (the granular grant/revoke on top of the role bundle).
 *  - **Delete** the per-user grants for someone who shouldn't have any
 *    dashboard access. Credentials live in the Support Center, so this
 *    only resets them to default `viewer`; deactivating the upstream
 *    account is still done in the Support Center admin UI.
 *
 * Talks to the dashboard server's own admin endpoints under
 * `/api/admin/*` — entirely separate from the CRM brain's user store.
 * Different scope, different endpoints, on purpose: the dashboard role
 * decides which top-nav tabs you see, while the CRM role decides what
 * you can do inside the CRM tab.
 *
 * This file is the **orchestrator** — the list query, the dialog state, the
 * paginated table, and the confirm/edit dialog wiring. The row + dialog +
 * permission-grid pieces, the nine `/api/admin/users/*` mutations, and the
 * pure helpers live as focused modules under `dashboard-users/`.
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import {
  ROLE_LABELS,
  type DashboardUser,
  type DashboardUserInvite,
  type ListDashboardUsersResponse,
} from "@xop/dashboard-shared";
import { UserPlus } from "lucide-react";
import { useMemo, useState } from "react";

import { useCan } from "../../auth/permissions.js";
import { useSessionUser } from "../../auth/AuthProvider.js";
import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "../../components/ui/pagination.js";
import { toast } from "../../components/ui/use-toast.js";
import { usePagedList } from "../../hooks/usePagedList.js";
import { ApiError, api } from "../../lib/api.js";
import { type CombinedRow } from "./dashboard-users/formatClient.js";
import { InviteRow } from "./dashboard-users/InviteRow.js";
import { InviteUserDialog } from "./dashboard-users/InviteUserDialog.js";
import { headCellSx } from "./dashboard-users/tableStyles.js";
import { useDashboardUserMutations } from "./dashboard-users/useDashboardUserMutations.js";
import { UserRow } from "./dashboard-users/UserRow.js";
import { RenameUserDialog, SetAvatarDialog, SetPasswordDialog } from "./dashboard-users/userDialogs.js";

export function DashboardUsersPanel(): JSX.Element {
  const canManagePermissions = useCan("admin:permissions");
  const session = useSessionUser();

  const {
    data,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => api<ListDashboardUsersResponse>("/api/admin/users"),
  });

  const {
    setRole,
    setOverrides,
    invite,
    renameUser,
    setPassword,
    setTwoFactor,
    sendInvite,
    deleteUser,
    cancelInvite,
    resendInvite,
  } = useDashboardUserMutations();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<DashboardUser | null>(
    null,
  );
  const [pendingCancelInvite, setPendingCancelInvite] =
    useState<DashboardUserInvite | null>(null);
  const [pendingRename, setPendingRename] = useState<DashboardUser | null>(null);
  const [pendingPassword, setPendingPassword] = useState<DashboardUser | null>(null);
  const [pendingTwoFactor, setPendingTwoFactor] = useState<{
    user: DashboardUser;
    method: "email" | "disabled";
  } | null>(null);
  const [pendingAvatar, setPendingAvatar] = useState<DashboardUser | null>(null);
  const [pendingInvite, setPendingInvite] = useState<DashboardUser | null>(null);

  // Combine invites + users into a single paginated list. Invites
  // come first so a freshly-sent invite is visible without scrolling.
  const rows = useMemo<CombinedRow[]>(
    () =>
      data
        ? [
            ...data.invites.map((i): CombinedRow => ({ kind: "invite", invite: i })),
            ...data.users.map((u): CombinedRow => ({ kind: "user", user: u })),
          ]
        : [],
    [data],
  );
  const paging = usePagedList(rows, 25);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {isLoading && <PageLoader />}
      {isError && !isLoading && (
        <Card>
          <CardContent sx={{ px: 2.5, py: 2 }}>
            <Box
              role="alert"
              sx={{
                display: "grid",
                gap: 1,
                borderRadius: 1,
                border: 1,
                borderColor: (t) => alpha(t.palette.error.main, 0.4),
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                px: 1.5,
                py: 1.5,
                fontSize: 14,
                color: "error.main",
              }}
            >
              <Box sx={{ fontWeight: 600 }}>Couldn&rsquo;t load dashboard users.</Box>
              <Box sx={{ fontSize: 12 }}>
                {loadError instanceof ApiError
                  ? loadError.message
                  : "Unknown error from /api/admin/users."}
              </Box>
              <Box>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    void refetch();
                  }}
                >
                  Retry
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      {data && (
        <Card>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: 1,
              borderColor: "divider",
              px: 2.5,
              py: 2,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
              Dashboard Users
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setInviteOpen(true)}
              aria-label="Invite a new dashboard user"
              startIcon={<UserPlus size={14} />}
            >
              Invite user
            </Button>
          </Box>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ "& td, & th": { fontSize: 14 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Email</TableCell>
                    <TableCell sx={headCellSx}>Role</TableCell>
                    <TableCell sx={headCellSx}>Permissions</TableCell>
                    <TableCell sx={headCellSx}>Client</TableCell>
                    <TableCell sx={headCellSx}>Last seen</TableCell>
                    <TableCell sx={[headCellSx, { textAlign: "right" }]}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paging.paged.map((row) =>
                    row.kind === "invite" ? (
                      <InviteRow
                        key={`invite-${row.invite.id}`}
                        invite={row.invite}
                        onCancel={() => setPendingCancelInvite(row.invite)}
                        onResend={() => resendInvite.mutateAsync(row.invite.id)}
                      />
                    ) : (
                      <UserRow
                        key={`user-${row.user.id}`}
                        user={row.user}
                        canManagePermissions={canManagePermissions}
                        canDelete={row.user.id !== session.id}
                        onRoleChange={(role) =>
                          setRole.mutate({ id: row.user.id, role })
                        }
                        onPermissionsChange={(permissions) =>
                          setOverrides.mutate({ id: row.user.id, permissions })
                        }
                        onRename={() => setPendingRename(row.user)}
                        onSetPassword={() => setPendingPassword(row.user)}
                        onResetTwoFactor={(method) =>
                          setPendingTwoFactor({ user: row.user, method })
                        }
                        onSendInvite={() => setPendingInvite(row.user)}
                        onSetAvatar={() => setPendingAvatar(row.user)}
                        onDelete={() => setPendingDeleteUser(row.user)}
                      />
                    ),
                  )}
                  {paging.totalCount === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}
                      >
                        No dashboard users yet. Use{" "}
                        <Box component="span" sx={{ fontWeight: 500 }}>Invite user</Box> to set up
                        the first account.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <Pagination
              page={paging.page}
              perPage={paging.perPage}
              totalCount={paging.totalCount}
              totalPages={paging.totalPages}
              entityNoun="accounts"
              hideIfSinglePage
              onPageChange={paging.setPage}
              onPerPageChange={paging.setPerPage}
            />
          </Box>
        </Card>
      )}

      <InviteUserDialog
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={async (input) => {
          await invite.mutateAsync(input);
        }}
        canSetOverrides={canManagePermissions}
        error={
          invite.isError && invite.error instanceof ApiError
            ? invite.error
            : null
        }
        isSubmitting={invite.isPending}
      />

      <ConfirmDialog
        isOpen={pendingDeleteUser !== null}
        onClose={() => setPendingDeleteUser(null)}
        title="Delete dashboard user?"
        description={
          pendingDeleteUser
            ? `Drops ${pendingDeleteUser.email}'s role + overrides from the dashboard. They can still sign in (credentials live in the Support Center) but will come back as the default Sales Manager with no overrides. Deactivate the upstream account in the Support Center to fully revoke access.`
            : ""
        }
        confirmLabel="Delete user"
        onConfirm={async () => {
          if (!pendingDeleteUser) return;
          await deleteUser.mutateAsync(pendingDeleteUser.id);
        }}
      />

      <ConfirmDialog
        isOpen={pendingCancelInvite !== null}
        onClose={() => setPendingCancelInvite(null)}
        title="Cancel pending invite?"
        description={
          pendingCancelInvite
            ? `Removes the pre-provisioned role for ${pendingCancelInvite.email}. If they sign in afterwards, they'll come back as the default Sales Manager instead of "${ROLE_LABELS[pendingCancelInvite.dashboard_role]}".`
            : ""
        }
        confirmLabel="Cancel invite"
        onConfirm={async () => {
          if (!pendingCancelInvite) return;
          await cancelInvite.mutateAsync(pendingCancelInvite.id);
        }}
      />

      <RenameUserDialog
        user={pendingRename}
        onClose={() => setPendingRename(null)}
        onSubmit={async (name) => {
          if (!pendingRename) return;
          await renameUser.mutateAsync({ id: pendingRename.id, name });
        }}
      />

      <SetPasswordDialog
        user={pendingPassword}
        onClose={() => setPendingPassword(null)}
        onSubmit={async (password) => {
          if (!pendingPassword) return;
          await setPassword.mutateAsync({ id: pendingPassword.id, password });
        }}
      />

      <ConfirmDialog
        isOpen={pendingTwoFactor !== null}
        onClose={() => setPendingTwoFactor(null)}
        variant={pendingTwoFactor?.method === "disabled" ? "destructive" : "warning"}
        title={
          pendingTwoFactor?.method === "disabled"
            ? "Reset 2FA (require re-enrollment)?"
            : "Reset 2FA to email codes?"
        }
        description={
          pendingTwoFactor
            ? pendingTwoFactor.method === "disabled"
              ? `Clears ${pendingTwoFactor.user.name}'s two-factor entirely. On their next sign-in they'll be required to enroll a new second factor (authenticator app or email codes).`
              : `Switches ${pendingTwoFactor.user.name} to email-based 2FA. Any authenticator app is removed and they'll get a 6-digit code by email at their next sign-in. They keep their current session — have them sign out and back in to apply it.`
            : ""
        }
        confirmLabel={pendingTwoFactor?.method === "disabled" ? "Reset 2FA" : "Reset to email"}
        onConfirm={async () => {
          if (!pendingTwoFactor) return;
          await setTwoFactor.mutateAsync({
            id: pendingTwoFactor.user.id,
            method: pendingTwoFactor.method,
          });
        }}
      />

      <SetAvatarDialog
        user={pendingAvatar}
        onClose={() => setPendingAvatar(null)}
      />

      {/* (Re)send the first-time sign-in invite to an existing account. Copy
          adapts to whether they've ever signed in: a gentle first invite for
          never-onboarded rows (Amreek & co.), a clear password-reset warning
          for someone already using the app. */}
      <ConfirmDialog
        isOpen={pendingInvite !== null}
        onClose={() => setPendingInvite(null)}
        variant={pendingInvite?.last_seen_at ? "destructive" : "warning"}
        title={
          pendingInvite?.last_seen_at
            ? "Re-send invite & reset password?"
            : "Send first-time sign-in invite?"
        }
        description={
          pendingInvite
            ? pendingInvite.last_seen_at
              ? `⚠ This resets ${pendingInvite.name}'s password to a new one-time code, signs them out of every device, and forces them to set a new password + 2FA the next time they sign in. Only do this if they're locked out or never finished onboarding.`
              : `Sends ${pendingInvite.name} their first-time sign-in invite. They'll get an email at ${pendingInvite.email} with a one-time password, then set their own password and enroll 2FA the first time they sign in to the desktop app.`
            : ""
        }
        confirmLabel={pendingInvite?.last_seen_at ? "Reset & send invite" : "Send invite"}
        onConfirm={async () => {
          if (!pendingInvite) return;
          const target = pendingInvite;
          try {
            await sendInvite.mutateAsync(target.id);
            toast({
              title: "Invite sent",
              description: `A one-time sign-in invite was emailed to ${target.email}.`,
              severity: "success",
            });
          } catch (e) {
            toast({
              title: "Couldn’t send invite",
              description:
                e instanceof ApiError ? e.message : "The identity service was unreachable.",
              severity: "error",
            });
            throw e; // keep the dialog open so the admin can retry
          }
        }}
      />
    </Box>
  );
}
